import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getUser } from '../../utils/auth';
import { documentFromState } from './formulaShape';
import { distributionFromAccounting } from './AccountingEditor';
import CalculationAgent from './CalculationAgent';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function CalculationLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const inPreview = location.pathname.startsWith('/procore/');
  const isAdmin = (getUser()?.role || 'user') === 'admin';
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pendingCreate, setPendingCreate] = useState(null);
  const creatingRef = useRef(false);
  const [accounts, setAccounts] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectChoice, setProjectChoice] = useState('');

  const load = (search) => {
    setLoading(true);
    const params = search ? { q: search } : {};
    axios.get(`${baseURL}/api/calculation-library`, { params })
      .then((response) => setRows(response.data?.calculations || []))
      .catch((err) => setError(err.response?.data?.error || 'Could not load the Calculation Library.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load('');
    Promise.all([
      axios.get(`${baseURL}/api/chartofaccounts`).catch(() => ({ data: [] })),
      axios.get(`${baseURL}/api/cost-types`).catch(() => ({ data: [] })),
    ]).then(([accountRes, typeRes]) => {
      const accountRows = Array.isArray(accountRes.data) ? accountRes.data : [];
      setAccounts(accountRows.filter((account) => account.status === 'active'));
      setCostTypes(Array.isArray(typeRes.data) ? typeRes.data : []);
    });
  }, []);

  const openPath = (vuid) => (
    inPreview ? `/procore/setup/calculation-library/${vuid}` : `/calculation-library/${vuid}`
  );

  const create = async (draft, slots) => {
    if (creatingRef.current) return;
    const calculationName = name.trim();
    if (!calculationName) {
      setPendingCreate({ draft, slots });
      setError('A calculation needs a name.');
      return;
    }
    creatingRef.current = true;
    setError('');
    try {
      const response = await axios.post(`${baseURL}/api/calculation-library`, {
        name: calculationName,
        description: draft?.description || '',
      });
      if (response.data.draft?.vuid) {
        await axios.put(`${baseURL}/api/calculation-library/versions/${response.data.draft.vuid}`, {
          document: documentFromState(draft?.parameters || [], draft?.namedValues || [], draft?.source, null, draft?.calculationType),
          distribution: distributionFromAccounting(draft?.accountingLines || [], draft?.namedValues || []),
        });
      }
      navigate(openPath(response.data.definition.vuid));
    } catch (err) {
      creatingRef.current = false;
      setPendingCreate({ draft, slots });
      setError(err.response?.data?.error || 'Could not create the calculation.');
    }
  };

  const beginAssign = async (row) => {
    setError('');
    setAssigning(row);
    setProjectChoice('');
    if (projects.length) return;
    try {
      const response = await axios.get(`${baseURL}/api/projects?per_page=100&lite=1`);
      const projectRows = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      setProjects(projectRows);
      if (projectRows[0]) setProjectChoice(projectRows[0].vuid);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load projects.');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.name}? It has not been assigned to any project.`)) return;
    setError('');
    try {
      await axios.delete(`${baseURL}/api/calculation-library/${row.vuid}`);
      load(query.trim());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete this calculation.');
    }
  };

  const assign = async (event) => {
    event.preventDefault();
    if (!assigning || !projectChoice) return;
    setError('');
    try {
      await axios.post(`${baseURL}/api/projects/${projectChoice}/calculations`, { definition_vuid: assigning.vuid });
      setAssigning(null);
      load(query.trim());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not assign this calculation.');
    }
  };

  const actions = isAdmin ? (
    <div className={inPreview ? 'pc-calc-actions' : 'flex gap-2'}>
      <button type="button" className="btn-claret" onClick={() => setCreating((current) => !current)}>New Calculation</button>
    </div>
  ) : null;

  return (
    <div className={inPreview ? 'pc-calc pc-calc-list' : undefined}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calculation Library</h1>
          <p className="text-sm text-gray-600 mt-1">Reusable calculations for this company. Each project keeps its own rate, source, destination, and accounts.</p>
        </div>
        {inPreview ? null : actions}
      </div>
      {inPreview ? actions : null}
      {error ? <div className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div> : null}
      {creating && isAdmin ? (
        <div className="bg-white rounded-xl shadow-lg p-4 mb-3 pc-calc-create">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Create a calculation</h2>
          <label className="pc-calc-create-name text-sm font-medium text-gray-700 mb-3">
            Name
            <input
              className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-md"
              placeholder="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <CalculationAgent
            asking
            name={name}
            setName={setName}
            description=""
            setDescription={() => {}}
            parameters={[]}
            setParameters={() => {}}
            namedValues={[]}
            setNamedValues={() => {}}
            accountingLines={[]}
            setAccountingLines={() => {}}
            accounts={accounts}
            costTypes={costTypes}
            source={null}
            setSource={() => {}}
            costCodes={[]}
            destination={null}
            onDestination={() => {}}
            onBuilt={create}
            finishLabel="Create"
          />
          {pendingCreate ? (
            <button type="button" className="btn-claret mt-3" onClick={() => create(pendingCreate.draft, pendingCreate.slots)}>Create</button>
          ) : null}
        </div>
      ) : null}
      <form
        className={inPreview ? 'pc-calc-toolbar' : 'bg-white rounded-xl shadow-lg p-6 mb-6 flex flex-wrap gap-3 items-end'}
        onSubmit={(event) => { event.preventDefault(); load(query.trim()); }}
      >
        {inPreview ? null : (
          <label className="text-sm font-medium text-gray-700 flex-1 min-w-[16rem]">
            Search
            <input
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        )}
        {inPreview ? (
          <input
            placeholder="Filter calculations"
            value={query}
            onChange={(event) => {
              const value = event.target.value;
              setQuery(value);
              load(value.trim());
            }}
          />
        ) : null}
        {inPreview ? null : <button type="submit" className="btn-claret">Search</button>}
      </form>
      {assigning ? (
        <form className="bg-white rounded-xl shadow-lg p-6 mb-6" onSubmit={assign}>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Assign {assigning.name}</h2>
          <p className="text-sm text-gray-600 mb-3">The project will use the latest published version. You can set its rate, source, and destination after assigning.</p>
          <div className="flex flex-wrap gap-3">
            <select className="px-3 py-2 border border-gray-300 rounded-md min-w-[16rem]" value={projectChoice} onChange={(event) => setProjectChoice(event.target.value)} required>
              <option value="">Choose a project</option>
              {projects.map((project) => (
                <option key={project.vuid} value={project.vuid}>{project.project_number} — {project.project_name}</option>
              ))}
            </select>
            <button type="submit" className="btn-claret" disabled={!projectChoice}>Assign to Project</button>
            <button type="button" className="px-4 py-2 border border-gray-300 rounded-md text-sm" onClick={() => setAssigning(null)}>Cancel</button>
          </div>
        </form>
      ) : null}
      {!loading && !rows.length ? (
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h2 className="text-lg font-semibold text-gray-900">No calculations in the library</h2>
          <p className="text-sm text-gray-600 mt-2 max-w-xl">Create a reusable calculation for items such as labor burden, insurance, allocations, or other recurring project accounting.</p>
          {isAdmin ? (
            <div className="mt-4 flex gap-3">
              <button type="button" className="btn-claret" onClick={() => setCreating(true)}>New Calculation</button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Calculation</th>
                <th className="px-4 py-3 font-medium">Published</th>
                <th className="px-4 py-3 font-medium">Draft</th>
                <th className="px-4 py-3 font-medium">Projects</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-4 text-gray-500" colSpan={6}>Loading…</td></tr>
              ) : rows.map((row) => (
                <tr key={row.vuid} className="border-t border-gray-200 align-top">
                  <td className="px-4 py-3">
                    <button type="button" className="font-medium text-gray-900 underline" onClick={() => navigate(openPath(row.vuid))}>
                      {row.name}
                    </button>
                    {row.description ? <div className="text-xs text-gray-500 mt-1 max-w-sm">{row.description}</div> : null}
                    {row.basis ? <div className="text-xs text-gray-600 mt-1">Basis: {row.basis}</div> : null}
                  </td>
                  <td className="px-4 py-3">{row.version_label || 'Not published'}</td>
                  <td className="px-4 py-3">{row.draft_version_number ? `v${row.draft_version_number}` : '—'}</td>
                  <td className="px-4 py-3">
                    <div>{row.project_count}</div>
                    {row.assignments_behind ? (
                      <div className="text-xs text-amber-800">Version update available for {row.assignments_behind}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <span className={inPreview ? `pc-pill ${row.status === 'active' ? 'active' : ''}` : undefined}>
                      {row.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                    <button type="button" className="underline" onClick={() => navigate(openPath(row.vuid))}>Open</button>
                    {isAdmin && row.version_label && row.status === 'active' ? (
                      <button type="button" className="underline" onClick={() => beginAssign(row)}>Assign</button>
                    ) : null}
                    {isAdmin && !row.project_count ? (
                      <button
                        type="button"
                        className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                        data-tooltip="Delete calculation"
                        aria-label={`Delete ${row.name}`}
                        onClick={() => remove(row)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
